/*
 * Ryan O'Dowd
 * 2025-08-02
 * © Copyright 2025 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import Select from '@components/common/Select';
import Text from '@components/common/Text';
import TextInput from '@components/common/TextInput';
import {
  adminEditTranslation,
} from '@utils/store/actions';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const EditBibleDialog = (props: EditBibleDialogProps) => {
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);
  const dispatch = hooks.useTypedDispatch();

  const locales = hooks.useTypedSelector((state) => state.locales);

  const [_message, setMessage] = useState('');
  const [_translationAbbreviation, setTranslationAbbreviation] = useState(props.translation.abbreviation);
  const [_translationName, setTranslationName] = useState(props.translation.name);
  const [_translationNameEnglish, setTranslationNameEnglish] = useState(props.translation.name_english);
  const [_translationLocaleId, setTranslationLocaleId] = useState(props.translation.locale_id);
  const [_translationUrl, setTranslationUrl] = useState(props.translation.url ?? '');
  const [_version, setVersion] = useState(utilities.lexigraphicToSemver(props.translation.available_in_version_lexigraphic) ?? '');

  return (
    <Dialog
      style={styles.container}
      visible={true}
      onDismiss={() => props.onClose()} // @TODO: ask about unsaved changes
    >
      <Dialog.Title>Edit Bible</Dialog.Title>
      <Dialog.Content>
        <TextInput
          value={_translationAbbreviation}
          mode='outlined'
          onChangeText={(text) => setTranslationAbbreviation(text)}
          label='Abbreviation'
          placeholder='e.g. NIV'
        />
        <TextInput
          value={props.translation.api_id}
          mode='outlined'
          disabled={true}
          label='API ID'
          placeholder='e.g. c1096b0470327a28-01'
        />
        <TextInput
          value={_translationName}
          mode='outlined'
          onChangeText={(text) => setTranslationName(text)}
          label='Local name'
          placeholder='e.g. Sveta Biblija'
        />
        <TextInput
          value={_translationNameEnglish}
          mode='outlined'
          onChangeText={(text) => setTranslationNameEnglish(text)}
          label='English Name'
          placeholder='e.g. Serbian Bible'
        />
        <Select
          label='Locale'// @TODO: i18n
          options={Object.values(locales).map((locale: any) => {
            return {
              label: locale.language_name,
              value: locale.id,
            };
          })}
          value={_translationLocaleId}
          onChange={(value) => setTranslationLocaleId(value)}
        />
        <TextInput
          value={_translationUrl}
          mode='outlined'
          onChangeText={(text) => setTranslationUrl(text)}
          label='Website'
          placeholder='e.g. https://www.biblica.com'
        />
        <TextInput
          value={_version}
          mode='outlined'
          onChangeText={(text) => setVersion(text)}
          label='Available in version'
          placeholder='e.g. 1.2.3'
        />
        {!!_message && <Text style={styles.note}>{_message}</Text>}
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          mode='text'
          onPress={() => props.onClose()}
        >
          {i18n('cancel')}
        </Button>
        <Button
          onPress={() => {
            if (!_translationAbbreviation || !_translationName || !_translationNameEnglish || !_translationLocaleId) {
              setMessage('All fields (except for website) are required.');
              return;
            }

            dispatch(adminEditTranslation(
              props.translation.id,
              _translationAbbreviation,
              _translationName,
              _translationNameEnglish,
              _translationLocaleId,
              _translationUrl,
              _version,
            ));

            props.onClose();
          }}
        >
          {i18n('save')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

class EditBibleDialogProps {
  onClose: () => void;
  translation: any;
};

export default EditBibleDialog;
